import { ChangeDetectionStrategy, Component, ElementRef, OnDestroy, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { AIFeedbackSubmission, AIFeedbackValue } from '@jms/model';
import { AIService } from '@workbuddy/ai/core/ai.service';
import { ToastService } from '@workbuddy/ui/services';
import { AgentUtility } from '../../agent.utility';

type FeedbackView = 'prompt' | 'negative';

/**
 * Rate-this-conversation prompt shown above the composer. A slim bar (thumbs up / down + dismiss):
 * thumbs-up submits positive feedback immediately (no dialog); thumbs-down opens a dialog asking
 * for an optional note. Feedback is thread-level — it attaches to the thread's latest completed
 * turn server-side. Emits `handled` once the user rates or dismisses so the host can stop showing it.
 */
@Component({
  selector: 'agent-thread-feedback',
  standalone: true,
  templateUrl: './thread.feedback.component.html',
  styleUrls: ['./thread.feedback.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentThreadFeedbackComponent implements OnDestroy {

  public readonly $threadId = input.required<string>({ alias: 'threadId' });
  /** Emitted when the user rates or dismisses — the host should suppress the prompt afterwards. */
  public readonly handled = output<void>();

  private readonly aiService = inject(AIService);
  private readonly toast = inject(ToastService);
  private readonly detailsInput = viewChild<ElementRef<HTMLTextAreaElement>>('detailsInput');

  protected readonly $view = signal<FeedbackView>('prompt');
  protected readonly $reason = signal('');
  protected readonly $submitting = signal(false);

  public constructor() {

    // Focus the note field whenever the negative dialog opens (deferred so the view exists first).
    effect(() => {
      if (this.$view() !== 'negative') return;
      queueMicrotask(() => this.detailsInput()?.nativeElement.focus());
    });

    // Escape must close the dialog WITHOUT minimizing the whole panel. The panel service
    // has a document-level (bubble-phase) Escape handler that minimizes the popup; it is
    // registered at app start, so it would otherwise run before this component. Listening
    // in the CAPTURE phase runs first, and stopPropagation() keeps the event from ever
    // reaching that bubble handler while our dialog is open.
    document.addEventListener('keydown', this.onDocumentEscapeCapture, true);
  }

  public ngOnDestroy(): void {

    document.removeEventListener('keydown', this.onDocumentEscapeCapture, true);
  }

  private readonly onDocumentEscapeCapture = (event: KeyboardEvent): void => {

    if (event.key !== 'Escape' || this.$view() !== 'negative') return;
    event.preventDefault();
    event.stopPropagation();
    this.cancel();
  };

  /** Thumbs-up: submit positive feedback straight away, no dialog. */
  protected ratePositive(): void {

    this.submit('positive', undefined);
  }

  /** Thumbs-down: open the dialog to ask for an optional note. */
  protected openNegative(): void {

    this.$reason.set('');
    this.$view.set('negative');
  }

  /** Dismiss the whole prompt without rating (the bar's ✕). */
  protected dismiss(): void {

    this.handled.emit();
  }

  /** Close the dialog and return to the bar without submitting. */
  protected cancel(): void {

    this.$view.set('prompt');
    this.$reason.set('');
  }

  protected onReasonInput(event: Event): void {

    this.$reason.set((event.target as HTMLTextAreaElement | null)?.value ?? '');
  }

  protected submitNegative(): void {

    this.submit('negative', this.$reason().trim() || undefined);
  }

  private submit(value: AIFeedbackValue, reason: string | undefined): void {

    if (this.$submitting()) return;
    this.$submitting.set(true);
    const submission: AIFeedbackSubmission = reason ? { value, reason } : { value };
    // Resolve BEFORE dismissing: `handled` is emitted only on a confirmed save, so a failed
    // request leaves the prompt (and any open dialog) in place to retry instead of silently
    // losing the rating. The component now outlives the POST, so the subscription completes
    // normally — no takeUntilDestroyed (which would cancel it) and no optimistic emit.
    this.aiService.submitThreadFeedback(AgentUtility.AGENT_ID, this.$threadId(), submission)
      .subscribe({
        next: response => {
          this.$submitting.set(false);
          if (response.flagged === false) {
            // Nothing was recorded — close the dialog but keep the prompt (don't suppress it).
            this.toast.info('Nothing to rate yet', 'There’s no completed reply on this chat to rate.');
            this.$view.set('prompt');
            return;
          }
          this.toast.success('Thanks for your feedback');
          this.handled.emit();
        },
        error: () => {
          this.$submitting.set(false);
          this.toast.error('Couldn’t save feedback', 'Please try again.');
        },
      });
  }
}
